import React, { useState, useEffect } from 'react';
import { RefreshCw, Play } from 'lucide-react';
import { getBaseUrl } from '../../../../shared/utils/apiConfig';

const getApiConfig = () => ({
  authToken: localStorage.getItem('astrapeak_auth_token'),
  baseUrl: getBaseUrl()
});

interface JobStatus {
  is_running: boolean;
  check_interval_hours: number;
  last_check_time: string | null;
  uptime_hours: number | null;
}

export const RetrainingJobs: React.FC = () => {
  const [status, setStatus] = useState<JobStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [tenantId, setTenantId] = useState('');

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const { authToken, baseUrl } = getApiConfig();
      const res = await fetch(`${baseUrl}/api/platform-admin/ml/retraining/jobs/status`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      setStatus(await res.json());
    } finally {
      setLoading(false);
    }
  };

  const triggerRetraining = async () => {
    if (!tenantId) {
      alert('Please enter tenant ID');
      return;
    }

    try {
      const { authToken, baseUrl } = getApiConfig();
      await fetch(`${baseUrl}/api/platform-admin/ml/retraining/jobs/trigger`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tenant_id: tenantId, bypass_cooldown: false })
      });
      alert('Retraining triggered successfully');
    } catch (err) {
      alert('Failed to trigger retraining');
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <RefreshCw className="w-6 h-6" />
        Auto-Retraining Jobs
      </h1>

      {status && (
        <div className="bg-white border rounded-lg p-6 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Job Status</p>
              <p className="text-lg font-semibold">{status.is_running ? 'Running' : 'Stopped'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Check Interval</p>
              <p className="text-lg font-semibold">{status.check_interval_hours}h</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Last Check</p>
              <p className="text-lg font-semibold">
                {status.last_check_time ? new Date(status.last_check_time).toLocaleString() : 'Never'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Uptime</p>
              <p className="text-lg font-semibold">
                {status.uptime_hours ? `${status.uptime_hours.toFixed(1)}h` : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border rounded-lg p-6">
        <h3 className="font-semibold mb-4">Manual Trigger</h3>
        <div className="flex gap-4">
          <input
            type="text"
            placeholder="Tenant ID"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className="flex-1 px-4 py-2 border rounded-lg"
          />
          <button
            onClick={triggerRetraining}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Play className="w-4 h-4" />
            Trigger Retraining
          </button>
        </div>
      </div>
    </div>
  );
};
